import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import dayjs from "dayjs";
import pick from "lodash/pick";
import {
    CampaignInquiriesQuery,
    CampaignInquiryQuery,
    CreateCampaignInquiryInput,
} from "src/graphql/generated/graphql";
import { createCampaignInquiryMutation } from "src/graphql/operations/campaignInquiries/mutations";
import { campaignInquiriesQuery, campaignInquiryQuery } from "src/graphql/operations/campaignInquiries/query";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";
import { GraphQLRequestError } from "src/graphql/utils/utils";
import { TranslationKeys } from "src/types/i18next";

import { handleError } from "../utils/error";
import { useAuthContext } from "./auth";

export const useCampaignInquiries = () => {
    const { client } = useAuthContext();

    const [campaignInquiries, setCampaignInquiries] = useState<CampaignInquiriesQuery["campaignInquiries"]["items"]>(
        []
    );
    const [isLoadingCampaignInquiries, setIsLoadingCampaignInquiries] = useState(false);

    const fetchCampaignInquiries = useCallback(
        async (nextToken?: string) => {
            setIsLoadingCampaignInquiries(true);

            let hasNext = false;
            try {
                const response = await graphqlFetch(campaignInquiriesQuery, { client: client?.id, nextToken });

                setCampaignInquiries((campaignInquiries) => [
                    ...campaignInquiries,
                    ...(response.campaignInquiries?.items || []),
                ]);

                if (response.campaignInquiries?.nextToken) {
                    hasNext = true;
                    fetchCampaignInquiries(response.campaignInquiries.nextToken);
                }
            } catch (err) {
                handleError(err, { context: { clientId: client?.id, nextToken } });
            } finally {
                if (!hasNext) {
                    setIsLoadingCampaignInquiries(false);
                }
            }
        },
        [client]
    );

    useEffect(() => {
        if (!client?.id) {
            return;
        }

        setCampaignInquiries([]);
        fetchCampaignInquiries();
    }, [fetchCampaignInquiries, client]);

    return {
        campaignInquiries,
        campaignInquiriesTotal: campaignInquiries?.length,
        isLoadingCampaignInquiries,
        reloadCampaignInquiries() {
            setCampaignInquiries([]);
            fetchCampaignInquiries();
        },
    };
};

export type CampaignInquiryQueryDataType = CampaignInquiryQuery["campaignInquiry"];

export const useCampaignInquiry = (campaignInquiryId: string) => {
    const [campaignInquiry, setCampaignInquiry] = useState<CampaignInquiryQueryDataType>(null);
    const [isLoadingCampaignInquiry, setIsLoadingCampaignInquiry] = useState(!!campaignInquiryId);

    const fetchCampaignInquiry = useCallback(async (campaignInquiryId: string) => {
        setIsLoadingCampaignInquiry(true);

        try {
            const response = await graphqlFetch(campaignInquiryQuery, { campaignInquiryId });

            setCampaignInquiry(response.campaignInquiry);
        } catch (err) {
            handleError(err, { context: { campaignInquiryId } });
        } finally {
            setIsLoadingCampaignInquiry(false);
        }
    }, []);

    useEffect(() => {
        if (!campaignInquiryId) {
            return;
        }

        fetchCampaignInquiry(campaignInquiryId);
    }, [campaignInquiryId, fetchCampaignInquiry]);

    return {
        campaignInquiry,
        isLoadingCampaignInquiry,
        reloadCampaignInquiry() {
            fetchCampaignInquiry(campaignInquiryId);
        },
    };
};

export const useCreateCampaignInquiry = () => {
    const { t } = useTranslation(["common"]);
    const [isCreatingCampaignInquiry, setIsCreatingCampaignInquiry] = useState(false);

    const createCampaignInquiry = useCallback(
        async (campaignInquiry: CreateCampaignInquiryInput) => {
            setIsCreatingCampaignInquiry(true);
            showNotification({
                color: "blue",
                message: t("campaigninquiry_saving"),
                loading: true,
            });

            try {
                campaignInquiry.products = (campaignInquiry?.products || []).map((product) => ({
                    ...product,
                    category: {
                        key: product?.category?.key,
                    },
                }));

                if (campaignInquiry?.settings?.openAt) {
                    campaignInquiry.settings.openAt = dayjs(campaignInquiry.settings.openAt).valueOf();
                }

                campaignInquiry.images = (campaignInquiry.images || []).map((image) => ({
                    ...image,
                    file: pick(image?.file, ["id", "objectKey"]),
                }));

                const response = await graphqlFetch(createCampaignInquiryMutation, { campaignInquiry });

                return response.createCampaignInquiry;
            } catch (err) {
                let errorMessage: TranslationKeys = "campaigninquiry_saving_failed";

                // The questionnaire cap is the one save failure we can name for the
                // client, and it is the common one: copying a campaign prefills every
                // question from the source, which can exceed the client's limit. The
                // instanceof guard is load-bearing — this used to read `err.errors`,
                // which GraphQLRequestError does not have, so the catch block itself
                // threw and the specific message was unreachable (TTV2-1659).
                if (
                    err instanceof GraphQLRequestError &&
                    err.errorsNormalized.some((error) => error.errorType === "LimitExceeded")
                ) {
                    errorMessage = "campaigninquiry_saving_failed_limit_exceeded";
                }

                handleError(err, {
                    message: { key: errorMessage, namespace: "common" },
                });
            } finally {
                setIsCreatingCampaignInquiry(false);
            }
        },
        [t]
    );

    return {
        isCreatingCampaignInquiry,
        createCampaignInquiry,
    };
};
